/*
 * Copyright (c) 2022 OTB Africa. All rights reserved. <https://otbafrica.com>
 *
 * Created Date: Tuesday, April 5th 2022, 11:40:46 pm
 * Author: OTB Africa, developers@otbafrica.com
 *
 * Use of this source code is governed by the license that can be found in
 * the LICENSE file or at https://developers.otbafrica.com/licenses/
 */

import React from "react";
import { useForm } from "react-hook-form";
import SellIcon from "@mui/icons-material/Sell";
import Stack from "@mui/material/Stack";
import Checkbox from "@mui/material/Checkbox";
import FormControlLabel from "@mui/material/FormControlLabel";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import {
  BillingSchedule,
  IPriceSettings,
  PriceSettingsValidationSchema,
  IPriceRule,
  CONDITION_LOGIC_TYPES,
  PriceRuleValidationSchema,
} from "lib/schemas";
import { ObjectKey } from "lib/types";
import { ValidationResolver } from "lib/validation";
import {
  OSForm,
  useControlledInput,
  FormInputCustomErrorWrapper,
  FormSubmitBubblingStopper,
  TextFormInput,
  DropDownFormInput,
  NestedOne2ManyPopupFormInput,
  RelatedObjectOSFormProps,
} from "ui-widgets/forms";
import { useOSFormContext } from "ui-widgets/forms/providers";
import {
  useToggleState,
  StyledOSDialog,
  IconText,
  InputHelpDialogWrapper,
  HelperText,
  EmphasisText,
  Image,
} from "ui-widgets";
import { ConditionsNestedOne2ManyPicker } from "common/components/conditions";
import { PricingChipButton } from "./PricingChipButton";

export const PriceSettings = () => {
  const { handleClose, handleOpen, open } = useToggleState();
  const { watch } = useOSFormContext<BillingSchedule>();
  const priceSettings: IPriceSettings = watch<keyof BillingSchedule>("price_settings");
  return (
    <Stack direction="row" spacing={2}>
      <PricingChipButton variant="outlined" color="primary" onClick={handleOpen} price_settings={priceSettings} />
      <PriceSettingsPopup open={open} handleClose={handleClose} />
    </Stack>
  );
};

const PriceSettingsPopup = ({ open, handleClose }) => {
  return (
    <StyledOSDialog
      popUpIcon={<SellIcon color="primary" />}
      popUpTitle="Price Settings"
      open={open}
      onClose={() => handleClose()}
    >
      <PriceSettingsForm handleClose={handleClose} />
    </StyledOSDialog>
  );
};

const PriceSettingsForm = ({ handleClose }) => {
  const {
    field: { onChange, value },
  } = useControlledInput<BillingSchedule>({ schema_field_key: "price_settings" });

  const priceSettings = value as IPriceSettings;

  const usePriceSettingsFormReturn = useForm<IPriceSettings>({
    defaultValues: priceSettings,
    mode: "onTouched",
    resolver: ValidationResolver(PriceSettingsValidationSchema),
  });

  const onChangeTax = (event: React.FocusEvent<HTMLInputElement>) => {
    const tax = parseFloat(event.target.value) / 100;
    usePriceSettingsFormReturn.setValue("tax", tax);
  };

  const handleValidForm = (data: IPriceSettings) => {
    onChange(data);
    handleClose();
  };

  const [watchedTax, watchedPriceRules] = usePriceSettingsFormReturn.watch<(keyof IPriceSettings)[]>([
    "tax",
    "price_rules",
  ]) as [number, IPriceRule[]];

  const omitBasicPrice = () => {
    const priceSettingValues = usePriceSettingsFormReturn.getValues();
    const { price, ...priceSettingValuesWithoutBasicPrice } = priceSettingValues;
    usePriceSettingsFormReturn.reset(priceSettingValuesWithoutBasicPrice);
  };

  React.useEffect(() => {
    if (watchedPriceRules?.length > 0) omitBasicPrice();
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, [watchedPriceRules?.length]);

  const priceSettingErrors = usePriceSettingsFormReturn.formState?.errors;

  return (
    <FormSubmitBubblingStopper>
      <OSForm
        onFormSubmit={usePriceSettingsFormReturn.handleSubmit(handleValidForm)}
        validationSchema={PriceSettingsValidationSchema}
        useFormReturn={usePriceSettingsFormReturn}
      >
        <Stack spacing={2}>
          {watchedPriceRules?.length ? null : (
            <div>
              <PriceInputField<IPriceSettings> schema_field_key="price" />
              <HelperText>
                Set a price here if you want a fixed value for this fee. For more complex pricing based on various
                conditions, use the pricing rules configuration.
              </HelperText>
            </div>
          )}
          <InputHelpDialogWrapper
            helpTitle={<>Tax Conifguration</>}
            helpContent={
              <>
                Set a tax percentage that will apply to your price, if any. The percentage you provide will be
                automatically added to the calculated base price that is exclusive of taxes. Onestop currently only
                supports base prices exclusive of taxes.
              </>
            }
          >
            <TextField
              defaultValue={watchedTax ? watchedTax * 100 : null}
              onChange={onChangeTax}
              fullWidth
              label="Tax (%)"
              type="number"
              helperText={
                priceSettingErrors?.tax && (
                  <FormInputCustomErrorWrapper>{priceSettingErrors.tax.message}</FormInputCustomErrorWrapper>
                )
              }
            />
          </InputHelpDialogWrapper>
          <NestedPricingRulesPopup />
        </Stack>
      </OSForm>
    </FormSubmitBubblingStopper>
  );
};

type PriceType = "number" | "equation";

const inputStyles: Record<PriceType, React.CSSProperties> = {
  equation: { background: "#ededed" },
  number: {},
};

const PriceInputField = <T,>({
  schema_field_key,
  helperText,
}: {
  schema_field_key: ObjectKey<T>;
  helperText?: React.ReactNode;
}) => {
  const { watch } = useOSFormContext();

  const priceValue = watch(schema_field_key as any);

  const defaultPriceType = Boolean(priceValue) && isNaN(Number(priceValue)) ? "equation" : "number";

  const [priceType, setPriceType] = React.useState<PriceType>(defaultPriceType);

  const inputLabel = priceType === "equation" ? "Price Equation" : "Price (Exlusive of Taxes)";

  const inputType = priceType === "equation" ? "text" : "number";

  const inputHelpText =
    priceType === "equation" ? (
      <>
        <p>
          Enter an equation for the price e.g. 20*3. Field tags are also supported in equations e.g.{" "}
          <EmphasisText>[field_tag]*189</EmphasisText>. Please note, you cannot use a field slug in an equation, to
          guarantee uniqueness of the value and provide a simple equation, we only support field tags in an equation.
        </p>
        <p>
          <EmphasisText>HINT: </EmphasisText> To get a field tag, open the field in the form builder, click on the
          hamburger menu next to the field title input field and click on "Copy Field Tag" and it will be copied to your
          clipboard.
        </p>
        <p>
          <Image src="/help-images/field-tag-copy.png" width={632} height={304} />
        </p>
      </>
    ) : (
      "Enter a number value for the price."
    );

  return (
    <>
      <div style={{ marginRight: "auto" }}>
        <FormControlLabel
          control={
            <Checkbox
              onChange={(event) => {
                setPriceType(event.target.checked ? "equation" : "number");
              }}
              checked={priceType === "equation"}
            />
          }
          label={<>Compute price based on an equation</>}
        />
      </div>
      <InputHelpDialogWrapper helpTitle={<>Price</>} helpContent={<>{inputHelpText}</>}>
        <TextFormInput<T>
          schema_field_key={schema_field_key}
          helperText={helperText}
          fullWidth
          label={inputLabel}
          type={inputType}
          sx={priceType === "equation" ? inputStyles.equation : inputStyles.number}
        />
      </InputHelpDialogWrapper>
    </>
  );
};

const NestedPricingRulesPopup = () => {
  return (
    <NestedOne2ManyPopupFormInput<IPriceSettings, IPriceRule>
      renderRelatedObjectForm={({ currentEditItem, handleClose, onChangeRelatedItem }) => (
        <PriceRulePopupForm
          defaultItemValue={currentEditItem}
          onFormCancel={handleClose}
          handleValidForm={onChangeRelatedItem}
        />
      )}
      popUpTitle={
        <Typography>
          <IconText
            icon={<SellIcon color="primary" />}
            textComponent={<Typography variant="h5">Price Rule</Typography>}
          />
        </Typography>
      }
      itemIdProperty={null}
      getOptionLabel={(option) =>
        option.name ? `${option.name} (Price: ${option.price})` : `Price Rule for ${option.price}`
      }
      schema_field_key="price_rules"
      label="Add New Price Rule"
      helperText={
        <>
          <p>
            Use pricing rules to configure how the price for this fee is computed with complex pricing logic based on
            application forms and/or worker's input in the workflow from task assessment forms.
          </p>
          <p>
            Note: Price rules have a higher priority over standalone prices. If you add one or more price rules, the
            price rules will be used to compute bill amounts and any standalone price will be ignored.
          </p>
        </>
      }
    />
  );
};

const PriceRulePopupForm = ({
  defaultItemValue,
  handleValidForm,
  onFormCancel,
}: RelatedObjectOSFormProps<IPriceRule>) => {
  const defaultNewPriceRuleValues: IPriceRule = defaultItemValue ?? {
    name: "",
    price: 0,
    condition_logic_type: "all",
    conditions: [],
  };

  const usePriceRuleFormReturn = useForm<IPriceRule>({
    defaultValues: defaultNewPriceRuleValues,
    mode: "onTouched",
    resolver: ValidationResolver(PriceRuleValidationSchema),
  });

  return (
    <OSForm
      onFormSubmit={usePriceRuleFormReturn.handleSubmit(handleValidForm)}
      validationSchema={PriceRuleValidationSchema}
      useFormReturn={usePriceRuleFormReturn}
      onFormCancel={onFormCancel}
    >
      <Stack spacing={2}>
        <TextFormInput<IPriceRule> schema_field_key="name" fullWidth label="Pricing Rule Name" />
        <PriceInputField<IPriceRule> schema_field_key="price" />

        <DropDownFormInput<IPriceRule>
          schema_field_key="condition_logic_type"
          options={CONDITION_LOGIC_TYPES}
          label="Apply pricing rule if"
          helperText="Specify which conditions should be met to apply the price rule"
        />

        <ConditionsNestedOne2ManyPicker<IPriceRule> schema_field_key="conditions" />
      </Stack>
    </OSForm>
  );
};
